import { HideIcon, Modal, TextInput, ViewIcon } from '@medeloop/medeloop-components-library';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { changePassword } from 'src/api/users';
import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { showToast } from 'src/store/toast';

interface ChangePasswordModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
}

export const UpdatePasswordModal = ({ isModalOpen, setIsModalOpen }: ChangePasswordModalProps) => {
  const { t } = useTranslation();
  const token = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const { accessToken } = useSelector(selectAccount);

  const [oldPassword, setOldPassword] = useState('');
  const [hideOldPassword, setHideOldPassword] = useState(true);

  const [newPassword, setNewPassword] = useState('');
  const [hideNewPassword, setHideNewPassword] = useState(true);

  const [confirmPassword, setConfirmPassword] = useState('');
  const [hideConfirmPassword, setHideConfirmPassword] = useState(true);

  const updatePasswordMutation = useMutation({
    mutationFn: () => {
      if (newPassword !== confirmPassword) {
        throw Error(t('new_passwords_dont_match'));
      }
      return changePassword(token, { accessToken, oldPassword, newPassword });
    },
    onSuccess: () => {
      dispatch(
        showToast({
          type: 'success',
          message: t('success_password_reseted'),
        }),
      );
    },
  });

  const closeAndResetChangePasswordModal = () => {
    setIsModalOpen(false);
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setHideOldPassword(true);
    setHideNewPassword(true);
    setHideConfirmPassword(true);
  };

  const handleChangePassword = async () => {
    await updatePasswordMutation.mutate();
    closeAndResetChangePasswordModal();
  };

  return (
    <Modal
      title={t('change_password')}
      isOpen={isModalOpen}
      actionType="primary"
      onCancel={() => closeAndResetChangePasswordModal()}
      actionText={t('change_password')}
      cancelText={t('cancel')}
      actionFunction={handleChangePassword}
      disabled={!(oldPassword && newPassword && confirmPassword)}
    >
      <div className="w-[500px]" />
      <TextInput
        type={hideOldPassword ? 'password' : 'text'}
        label={t('old_password')}
        value={oldPassword}
        onChange={(e) => setOldPassword(e.target.value)}
        postChildren={
          <div onClick={() => setHideOldPassword(!hideOldPassword)}>
            {hideOldPassword ? (
              <ViewIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <HideIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        }
      />
      <TextInput
        type={hideNewPassword ? 'password' : 'text'}
        label={t('new_password')}
        value={newPassword}
        onChange={(e) => setNewPassword(e.target.value)}
        postChildren={
          <div onClick={() => setHideNewPassword(!hideNewPassword)}>
            {hideNewPassword ? (
              <ViewIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <HideIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        }
      />
      <TextInput
        type={hideConfirmPassword ? 'password' : 'text'}
        label={t('confirm_password')}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        postChildren={
          <div onClick={() => setHideConfirmPassword(!hideConfirmPassword)}>
            {hideConfirmPassword ? (
              <ViewIcon className="h-5 w-5 text-gray-400" />
            ) : (
              <HideIcon className="h-5 w-5 text-gray-400" />
            )}
          </div>
        }
      />
    </Modal>
  );
};
